/**
 * 需求来源：docs/合作页、CJ Entity、Ads开户落地页3个/AI提示词.md（第2次提交 任务1）、advertisingservice.html。
 * Section 1：cj-premium-ad-hero 首屏。
 * 深色高端风：图片背景 + 装饰 SVG（CSS 已 display:none，保留以忠实还原 DOM）+ 玻璃面板 4 项卖点。
 * 主 CTA「Get Started」打开工单弹窗；Email 走 mailto、WhatsApp 走 wa.me 直链（沿用目标页）。
 * 注意事项：所有文案经 ConstantsContext 取词（i18next）；SVG/颜色为视觉资产，保留在组件内。
 */
import React, { useContext } from 'react';
import TicketContext from './TicketContext';
import ConstantsContext from './ConstantsContext';
import { LINKS } from '../constants';
import { mix } from '../colorMix';
// 260716 需求(68)：hero 人像背景改为内联 style 设置 background-image。
//   原在 index.less 外链 CSS 里 url(~asset/..)，CDN 部署时 CSS 位于 test-frontend.../web-egg/ssr-client/，
//   其内部 url 相对 CDN 域解析会丢 /web-egg 前缀→404、人像不显示（其它 <img> 相对页面源站解析故正常）。
//   内联 style 的 url() 相对页面文档源站解析（同 <img>），/ssr-client/images/.. 在源站可达→修复。
import cleanBgImg from 'asset/images/advertisingservice/multi-platform.jpg';

// 4 项卖点的图标顺序与 HERO.features 一一对应
var FEATURE_ICONS = [
  { color: '#f1d7a7', svg: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M7 7h4V3M17 17h-4v4M17 7h4v4M7 17H3v-4" /><circle cx="12" cy="12" r="3" /></svg>) },
  { color: '#b9a1ff', svg: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m4 16 5-5 4 4 7-8" /><path d="M16 7h4v4" /></svg>) },
  { color: '#7ddff3', svg: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 13a7 7 0 0 1 14 0" /><path d="M5 13v5h3v-5H5ZM16 13v5h3v-5h-3Z" /><path d="M16 18c0 2-1.5 3-4 3" /></svg>) },
  { color: '#d9c7ff', svg: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8" /><path d="m8.5 12 2.2 2.2 4.8-4.8" /></svg>) }
];

// 由图标主色预算派生 CSS 变量（等价原 less 的 color-mix，供旧机走 var 精确取色）
function iconVars(color) {
  if (!color) return {};
  return {
    '--icon': color,
    '--icon-bd': mix(color, 'transparent', 24), // 描边：icon 24% 透明
    '--icon-bg': mix(color, 'transparent', 11), // 底色：icon 11% 透明
  };
}

function HeroSection() {
  const openTicket = useContext(TicketContext);
  const { HERO } = useContext(ConstantsContext);
  return (
    <section className="cj-premium-ad-hero">
      <div className="cj-premium-ad-clean-bg" style={{ backgroundImage: 'url(' + cleanBgImg + ')' }} aria-hidden="true" />
      <div className="cj-premium-ad-bg-art" aria-hidden="true">
        <svg viewBox="0 0 1920 920" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="cjBgBase" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#080910" />
              <stop offset="48%" stopColor="#141421" />
              <stop offset="100%" stopColor="#21192b" />
            </linearGradient>
            <radialGradient id="cjGlowGold" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#f1d7a7" stopOpacity=".44" />
              <stop offset="58%" stopColor="#d2a66f" stopOpacity=".12" />
              <stop offset="100%" stopColor="#d2a66f" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="cjGlowViolet" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#a989ff" stopOpacity=".52" />
              <stop offset="55%" stopColor="#7454d5" stopOpacity=".14" />
              <stop offset="100%" stopColor="#7454d5" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="cjAuroraGold" x1="0" y1="0" x2="1" y2=".7">
              <stop offset="0%" stopColor="#f6e3bd" stopOpacity=".04" />
              <stop offset="45%" stopColor="#e7bf78" stopOpacity=".17" />
              <stop offset="100%" stopColor="#8b5d39" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="cjAuroraViolet" x1="0" y1=".2" x2="1" y2=".8">
              <stop offset="0%" stopColor="#6448b9" stopOpacity="0" />
              <stop offset="48%" stopColor="#9d7cff" stopOpacity=".22" />
              <stop offset="100%" stopColor="#7ddff3" stopOpacity=".03" />
            </linearGradient>
            <linearGradient id="cjLineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f1d7a7" stopOpacity="0" />
              <stop offset="45%" stopColor="#f1d7a7" stopOpacity=".48" />
              <stop offset="100%" stopColor="#9d7cff" stopOpacity="0" />
            </linearGradient>
            <filter id="cjBlur70" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="70" />
            </filter>
            <filter id="cjBlur30" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="30" />
            </filter>
            <filter id="cjNodeGlow" x="-200%" y="-200%" width="400%" height="400%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <pattern id="cjFineGrid" width="70" height="70" patternUnits="userSpaceOnUse">
              <path d="M70 0H0V70" fill="none" stroke="#ffffff" strokeOpacity=".028" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="1920" height="920" fill="url(#cjBgBase)" />
          <rect width="1920" height="920" fill="url(#cjFineGrid)" />
          <g filter="url(#cjBlur70)" opacity=".95">
            <ellipse className="cj-bg-aurora" cx="340" cy="270" rx="330" ry="210" fill="url(#cjGlowGold)" />
            <ellipse className="cj-bg-aurora alt" cx="1500" cy="230" rx="420" ry="260" fill="url(#cjGlowViolet)" />
            <ellipse className="cj-bg-aurora" cx="1340" cy="810" rx="420" ry="210" fill="url(#cjGlowGold)" opacity=".45" />
          </g>
          <path className="cj-bg-aurora" d="M-120 650C260 410 490 520 810 310C1110 115 1390 100 2030 230V930H-120Z" fill="url(#cjAuroraViolet)" opacity=".72" />
          <path className="cj-bg-aurora alt" d="M-80 200C340 60 570 270 900 180C1290 75 1570 20 1990 165V-40H-80Z" fill="url(#cjAuroraGold)" opacity=".58" />
          <g opacity=".48" fill="none">
            <path className="cj-network-line" d="M980 735C1150 560 1280 620 1420 450C1540 305 1695 340 1900 180" stroke="url(#cjLineGradient)" strokeWidth="1.4" />
            <path className="cj-network-line slow" d="M835 860C1080 660 1190 730 1360 570C1530 410 1670 430 1950 315" stroke="url(#cjLineGradient)" strokeWidth="1" />
            <path className="cj-network-line" d="M1100 150C1230 240 1330 205 1450 300C1570 395 1700 330 1890 460" stroke="url(#cjLineGradient)" strokeWidth="1.1" />
          </g>
          <g filter="url(#cjNodeGlow)">
            <circle className="cj-network-node" cx="1040" cy="677" r="4.5" fill="#f1d7a7" />
            <circle className="cj-network-node" cx="1225" cy="588" r="4" fill="#a989ff" />
            <circle className="cj-network-node" cx="1397" cy="478" r="5" fill="#7ddff3" />
            <circle className="cj-network-node" cx="1563" cy="368" r="4" fill="#f1d7a7" />
            <circle className="cj-network-node" cx="1745" cy="292" r="5" fill="#a989ff" />
            <circle className="cj-network-node" cx="1280" cy="250" r="4" fill="#7ddff3" />
            <circle className="cj-network-node" cx="1512" cy="350" r="4.5" fill="#f1d7a7" />
          </g>
          <g className="cj-light-streak" filter="url(#cjBlur30)">
            <path d="M1260 40L1720 870" stroke="#a989ff" strokeOpacity=".08" strokeWidth="34" />
            <path d="M1390 -20L1850 810" stroke="#f1d7a7" strokeOpacity=".05" strokeWidth="22" />
          </g>
        </svg>
      </div>
      <span className="cj-premium-ad-orb one" />
      <span className="cj-premium-ad-orb two" />

      <div className="cj-premium-ad-container">
        <div className="cj-premium-ad-grid">
          <div className="cj-premium-ad-copy">
            <h1 className="cj-premium-ad-title">
              {HERO.title} <span className="accent">{HERO.titleAccent}</span> {HERO.titleTail}
            </h1>

            <p className="cj-premium-ad-description">{HERO.description}</p>

            <div className="cj-premium-ad-actions">
              <a className="cj-premium-ad-btn primary" href="#get-started" onClick={(e) => { e.preventDefault(); openTicket(); }}>
                {HERO.ctaGetStarted}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>

              <a className="cj-premium-ad-btn contact email" href={LINKS.email}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m4 7 8 6 8-6" />
                </svg>
                {HERO.contactEmail}
              </a>

              <a className="cj-premium-ad-btn contact whatsapp" href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer">
                {/* 260715：WhatsApp 图标改用站点 iconfont &#xe7c2;（），颜色沿用 #61dfa7 */}
                <i className="iconfont" aria-hidden="true">{''}</i>
                {HERO.contactWhatsapp}
              </a>
            </div>

            <div className="cj-premium-ad-note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 3 5 6v5c0 4.4 2.8 8.4 7 10 4.2-1.6 7-5.6 7-10V6l-7-3Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              {HERO.note}
            </div>
          </div>

          <div className="cj-premium-ad-visual">
            <div className="cj-premium-ad-glow" />
            <div className="cj-premium-ad-panel">
              <div className="cj-premium-ad-panel-head">
                <h2 className="cj-premium-ad-panel-title">{HERO.panelTitle}</h2>
                <span className="cj-premium-ad-panel-tag">{HERO.panelTag}</span>
              </div>

              <div className="cj-premium-ad-feature-grid">
                {HERO.features.map(function (feature, i) {
                  var icon = FEATURE_ICONS[i] || {};
                  return (
                    <article className="cj-premium-ad-feature" key={feature.h3}>
                      <div className="cj-premium-ad-icon" style={iconVars(icon.color)}>{icon.svg}</div>
                      <h3>{feature.h3}</h3>
                      <p>{feature.p}</p>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
